import MapboxDraw, { DrawMode } from '@mapbox/mapbox-gl-draw';
import { Feature, Polygon } from 'geojson';
import { useEffect, useState } from 'react';
import { useControl, useMap } from 'react-map-gl/mapbox';
import { useRecoilState } from 'recoil';
import { useCamera } from '../../../hooks/useCamera';
import { mapDrawModeState, mapNewFeatureState } from '../../../storage/map';
import { MapDrawMode } from '../../../types/app';
// @ts-ignore
import DrawRectangle from 'mapbox-gl-draw-rectangle-mode';
import { DRAW_MODE_CIRCLE, DRAW_MODE_POLYGON } from '../../../constants/map';
import useEffectOnce from '../../../hooks/useEffectOnce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { Point } from '../../../types/geoModels';
import CoreCircleControl from './CoreCircleControls';
import CoreMapDrawOptions from './CoreMapDrawOptions';

type Props = Readonly<{
  updateKey?: string;
  polygon?: Polygon;
  updatedPolygon?: Polygon;
  onUpdatePolygon: (newPolygon?: Polygon) => void;
  onUpdateGeoreference?: (point: Point) => void;
  onUpdateGeoreferenceFromPolygon?: (point?: Polygon) => void;
  initialDrawMode?: MapDrawMode;
}>;

function CoreDrawControls({
  updateKey,
  polygon,
  updatedPolygon,
  onUpdatePolygon,
  onUpdateGeoreference,
  onUpdateGeoreferenceFromPolygon,
  initialDrawMode = DRAW_MODE_POLYGON,
}: Props) {
  const { map } = useMap();
  const [drawMode, setDrawMode] = useRecoilState(mapDrawModeState);
  const [ready, setReady] = useState(false);
  const [newFeature, setNewFeature] = useRecoilState(mapNewFeatureState);
  const { fitToLngLats } = useCamera();

  /* DRAW OBJECT */
  const draw: MapboxDraw & {
    changeMode: (mode: DrawMode | MapDrawMode, opts?: object, eventOpts?: object) => void;
  } = useControl<
    Omit<MapboxDraw, 'onAdd' | 'onRemove'> & { onAdd: (context: any) => HTMLElement; onRemove: (context: any) => void }
  >(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        controls: {},
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle: DrawRectangle,
        },
      }),
    ({ map }) => {
      map.on('draw.create', onCreate);
      map.on('draw.update', onUpdate);
      map.on('draw.delete', onDelete);
      map.on('draw.selectionchange', onSelectionChange);
      setReady(true);
    },
    ({ map }) => {
      map.off('draw.create', onCreate);
      map.off('draw.update', onUpdate);
      map.off('draw.delete', onDelete);
      map.off('draw.selectionchange', onSelectionChange);
      setReady(false);
    },
    {},
  );
  /* END DRAW OBJECT */

  /* CLEAR DRAW ON FENCE CHANGED */
  useUpdateEffect(() => {
    if (!ready) return;
    draw.deleteAll();
  }, [updateKey]);
  /* END CLEAR DRAW ON FENCE CHANGED */

  /* RESET DRAW MODE ON FENCE CHANGED */
  useEffect(() => {
    if (updateKey !== undefined) {
      setDrawMode(initialDrawMode);
    } else {
      setDrawMode(undefined);
    }
  }, [updateKey]);
  /* END RESET DRAW MODE ON FENCE CHANGED */

  /* HANDLE DRAW MODE CHANGES */
  useUpdateEffect(() => {
    if (!map) {
      return;
    }
    if (updateKey !== undefined) {
      if (drawMode === DRAW_MODE_CIRCLE) {
        map.getCanvas().style.cursor = 'crosshair';
      }
      if (drawMode !== DRAW_MODE_POLYGON || polygon !== updatedPolygon) {
        onUpdatePolygon(undefined);
        onUpdateGeoreferenceFromPolygon?.();
        draw.deleteAll();
      }
      if (drawMode && drawMode !== DRAW_MODE_CIRCLE && (polygon !== updatedPolygon || !polygon)) {
        draw.changeMode(drawMode);
      }
    } else {
      draw.deleteAll();
    }
    return () => {
      map.getCanvas().style.cursor = '';
    };
  }, [drawMode]);
  /* END HANDLE DRAW MODE CHANGES */

  /* CENTER COORDS */
  useEffect(() => {
    if (updatedPolygon?.coordinates[0].length) {
      fitToLngLats(updatedPolygon.coordinates[0]);
    }
  }, [updateKey, updatedPolygon, fitToLngLats]);
  /* END CENTER COORDS */

  /* HANDLE POLYGONS WHEN NOT CIRCLE MODE */
  useEffect(() => {
    if (!ready) {
      return;
    }
    if (drawMode !== DRAW_MODE_CIRCLE) {
      if (updatedPolygon) {
        if (polygon === updatedPolygon) {
          draw.deleteAll();
          const feature: GeoJSON.Feature = {
            type: 'Feature',
            geometry: updatedPolygon,
            properties: {},
          };
          const ids = draw.add(feature);
          draw.changeMode('direct_select', { featureId: ids[0] });
        } else {
          // Polygon changed
        }
      } else if (!updatedPolygon) {
        draw.deleteAll();
        draw.changeMode(drawMode || initialDrawMode);
      }
    }
  }, [ready, updatedPolygon, polygon]);
  /* END HANDLE POLYGONS WHEN NOT CIRCLE MODE */

  /* CLEANUP */
  useEffectOnce(() => {
    return () => {
      if (ready) {
        draw.deleteAll();
      }
    };
  });
  /* END CLEANUP */

  /* MANAGE FEATURES FROM DRAW */
  useUpdateEffect(() => {
    if (!newFeature) return;
    if (updateKey !== undefined) {
      onUpdatePolygon(newFeature.geometry as Polygon);
      onUpdateGeoreferenceFromPolygon?.(newFeature.geometry as Polygon);
    }
  }, [newFeature]);
  /* END MANAGE FEATURES FROM DRAW */

  /* DRAW EVENTS */
  const onUpdate = (ev: { features: Array<Feature>; action: 'move' | 'change_coordinate' }) => {
    setNewFeature(ev.features[0]);
  };

  const onCreate = (ev: { features: Array<Feature> }) => {
    setNewFeature(ev.features[0]);
  };

  const onDelete = (_: any) => {};

  const onSelectionChange = (_: any) => {};
  /* END DRAW EVENTS */

  return (
    <>
      <CoreMapDrawOptions
        onUpdatePolygon={(polygon) => {
          onUpdatePolygon(polygon);
          onUpdateGeoreferenceFromPolygon?.(polygon);
        }}
        polygon={polygon}
      />
      {drawMode === DRAW_MODE_CIRCLE && (
        <CoreCircleControl
          onUpdatePolygon={onUpdatePolygon}
          onUpdateGeoreference={onUpdateGeoreference}
        />
      )}
    </>
  );
}

export default CoreDrawControls;
